import {
  AccountSiteBUDto,
  BusinessType,
  BusinessUnitInterface,
} from '../interfaces'

/**
 * Resuelve las tiendas de DESTINO que un contrato corporativo puede usar.
 *
 * Espeja exactamente la regla de ApiCore en
 * TEALCA.ApiCore/Controllers/ShipmentController.cs:396-419
 *
 *   allowAnyBU = Model == null || !Model.Any() || Model.All(b => b.IsSource)
 *
 * ...para que el desplegable y el servidor nunca puedan estar en desacuerdo.
 *
 *   rows === undefined ──▶ aún no se consultó ──▶ catálogo por defecto, restricted: false
 *           │
 *           ▼
 *   rows.some(r => !r.isSource) ?          ◀── el disparador corre sobre las filas CRUDAS,
 *           │                                  igual que All(b => b.IsSource) en ApiCore.
 *           ├── NO  ──▶ catálogo por defecto, restricted: false
 *           │           (descarta KK/NN, descarta isLocked, deja EXTERNAL|INTERNAL)
 *           │
 *           └── SÍ  ──▶ restricted: true
 *                       options = filas usables → Map por buCode normalizado
 *                                 contra el catálogo COMPLETO
 *                                 (ignora los filtros de buTypeID y isLocked)
 *                       sin match en catálogo ──▶ console.warn + descartar
 *                       NUNCA se fabrica una tienda: las coordenadas alimentan
 *                       getTier() y una tienda inventada tarifica mal.
 *
 * Divergencia intencional: ApiCore sólo valida pertenencia e IsLocked, nunca
 * statusID ni la ventana de fechas. Aquí somos MÁS estrictos, que es el lado
 * seguro (nunca produce un 400). Registrado en TODOS.md.
 */

export const ACCOUNT_SITE_BU_ACTIVE_STATUS = 1

/** Códigos que nunca son destinos válidos, heredado del filtro original. */
const EXCLUDED_BU_CODES = ['KK', 'NN']

export interface DestinationBusinessUnits {
  /** Tiendas seleccionables. Sin ordenar: cada pantalla aplica su propio sort. */
  options: BusinessUnitInterface[]
  /** true cuando el contrato trae al menos una fila de destino (isSource=false). */
  restricted: boolean
}

/**
 * ApiCore serializa en camelCase (`AddControllers()` por defecto), pero el DTO
 * hereda de BaseEntity y redeclara `public new string BUCode`, así que leemos
 * ambas formas por si la serialización cambia bajo un upgrade.
 */
interface RawAccountSiteBU {
  BUCode?: string
  IsSource?: boolean
  IsLocked?: boolean
  StatusID?: number | null
}

const readBuCode = (row: AccountSiteBUDto): string =>
  (row as RawAccountSiteBU).BUCode ?? row.buCode ?? ''

const readIsSource = (row: AccountSiteBUDto): boolean =>
  Boolean((row as RawAccountSiteBU).IsSource ?? row.isSource)

const readIsLocked = (row: AccountSiteBUDto): boolean =>
  Boolean((row as RawAccountSiteBU).IsLocked ?? row.isLocked)

const readStatusID = (row: AccountSiteBUDto): number | null | undefined =>
  (row as RawAccountSiteBU).StatusID ?? row.statusID

const normalizeCode = (code?: string | null): string =>
  (code ?? '').trim().toLowerCase()

/**
 * Compara por día calendario, no por instante. `fromDate`/`toDate` vienen de un
 * DateTime de .NET sin zona horaria; parsear con `new Date()` movería el límite
 * un día para un cliente en Venezuela, así que leemos el YYYY-MM-DD literal.
 */
const calendarDayFromText = (value?: string | null): number | null => {
  if (!value) return null
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (iso) {
    return Number(iso[1]) * 10000 + Number(iso[2]) * 100 + Number(iso[3])
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return calendarDayFromDate(parsed)
}

const calendarDayFromDate = (value: Date): number =>
  value.getFullYear() * 10000 + (value.getMonth() + 1) * 100 + value.getDate()

/**
 * ¿Esta fila del contrato habilita hoy una tienda de destino?
 *
 * `now` se recibe por parámetro para que las pruebas no dependan del reloj.
 */
export const isContractRowUsable = (
  row: AccountSiteBUDto,
  now: Date
): boolean => {
  if (readIsSource(row)) return false
  if (readIsLocked(row)) return false

  const status = readStatusID(row)
  if (status != null && status !== ACCOUNT_SITE_BU_ACTIVE_STATUS) return false

  const today = calendarDayFromDate(now)

  const from = calendarDayFromText(row.fromDate)
  if (from != null && today < from) return false

  // toDate nulo o ausente = vigencia abierta.
  const to = calendarDayFromText(row.toDate)
  if (to != null && today > to) return false

  return true
}

/** El filtro histórico del catálogo, idéntico al que vivía duplicado en 4 pantallas. */
const defaultCatalogOptions = (
  catalog: BusinessUnitInterface[]
): BusinessUnitInterface[] => {
  const seen = new Set<number>()
  const options: BusinessUnitInterface[] = []

  catalog.forEach((businessUnit) => {
    if (EXCLUDED_BU_CODES.includes(businessUnit.buCode)) return
    if (businessUnit.isLocked) return
    if (
      businessUnit.buTypeID !== BusinessType.EXTERNAL &&
      businessUnit.buTypeID !== BusinessType.INTERNAL
    ) {
      return
    }
    if (seen.has(businessUnit.buid)) return

    seen.add(businessUnit.buid)
    options.push(businessUnit)
  })

  return options
}

export const resolveDestinationBusinessUnits = (
  catalog: BusinessUnitInterface[],
  rows: AccountSiteBUDto[] | undefined,
  now: Date
): DestinationBusinessUnits => {
  // Todavía no se consultó el contrato: comportamiento de hoy.
  if (rows === undefined) {
    return { options: defaultCatalogOptions(catalog), restricted: false }
  }

  // El disparador corre sobre las filas CRUDAS. Vigencia y bloqueo deciden QUÉ
  // tiendas se ofrecen, nunca SI el contrato restringe. Un contrato cuya única
  // fila de destino está bloqueada queda restringido con cero destinos válidos,
  // y eso es lo correcto: es exactamente lo que ApiCore va a rechazar.
  const hasDestinationRow = rows.some((row) => !readIsSource(row))
  if (!hasDestinationRow) {
    return { options: defaultCatalogOptions(catalog), restricted: false }
  }

  // Índice sobre el catálogo COMPLETO: las tiendas del contrato deben poder
  // saltarse los filtros de buTypeID (las virtuales son 140/160) y de isLocked.
  const catalogByCode = new Map<string, BusinessUnitInterface>()
  catalog.forEach((businessUnit) => {
    const key = normalizeCode(businessUnit.buCode)
    if (key && !catalogByCode.has(key)) catalogByCode.set(key, businessUnit)
  })

  const seen = new Set<number>()
  const options: BusinessUnitInterface[] = []

  rows.forEach((row) => {
    if (!isContractRowUsable(row, now)) return

    const buCode = readBuCode(row)

    // KK/NN son códigos centinela que TODAS las pantallas descartan (y también
    // Innovus). El contrato puede listarlos igual (el AccountSite 1772 lista NN)
    // y ApiCore los aceptaría, pero ofrecerlos rompería la convención de la
    // aplicación. Mismo criterio deliberado que con statusID y las fechas:
    // somos más estrictos que el servidor, que es el lado seguro.
    if (EXCLUDED_BU_CODES.includes(buCode.trim())) return

    const businessUnit = catalogByCode.get(normalizeCode(buCode))

    if (!businessUnit) {
      console.warn(
        `[destinos-contrato] buCode del contrato sin match en el catálogo: ${buCode}`
      )
      return
    }

    if (seen.has(businessUnit.buid)) return

    seen.add(businessUnit.buid)
    options.push(businessUnit)
  })

  return { options, restricted: true }
}
